import React, { useEffect, useRef, useState } from 'react'
import SoloRankedGame from '../../../solo/SoloRankedGame'
import { Item } from '@components/common/ItemGuessBox'
import EndGameView from './EndGameView'
import { DailyListResult } from 'src/pages/Dashboard'
import { UserState } from 'src/app/AppContext'
import { AlertInput } from '@components/common/Alert'
import { submitDailyCompletion } from 'src/api/lists'
import { submitQuestionGuess } from 'src/api/polls'
import { awardSoloPlay } from 'src/api/user'
import { UserSoloStats } from 'src/api/types/users'
import { err } from 'src/utils/functions'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SoloPlayView = (props: any) => {
  const {
    gameState,
    mode,
    multiMode,
    fullScreen,
    listItems,
    soloGameKey,
    topic,
    loading,
    activePollId,
    sounds,
    handleEndGame,
    handleNewGame,
    handlePreScreen,
    setGameState,
    selectedList,
    dailyList,
    dailyResult,
    setDailyResult,
    handlePlayedList,
    handleRestartGame,
    handleCreateList,
    handleRateList,
    setRateAlert,
    historyPush,
    listRated,
    appUserId,
    user,
    doCreate,
    onHasGuessChange,
    dailyProgress,
    onDailyProgressChange,
    soloStats,
    soloXpAwarded,
    onSoloStatsUpdate,
    onSoloXpAwarded,
  } = props

  const [soloGuessCount, setSoloGuessCount] = useState(0)
  const activeQuestionSession = useRef<string>()
  activeQuestionSession.current = listItems[0]?.question?.sessionId
  useEffect(
    () => () => {
      activeQuestionSession.current = undefined
    },
    [],
  )
  const [soloResult, setSoloResult] = useState<'active' | 'win' | 'loss'>(
    'active',
  )
  // eslint-disable-next-line react-hooks/purity
  const [startedAtMs, setStartedAtMs] = useState<number>(Date.now())

  useEffect(() => {
    if (gameState !== 'pre') return

    setSoloGuessCount(0)
    setSoloResult('active')
    setStartedAtMs(Date.now())
    onHasGuessChange?.(false)
    onSoloXpAwarded?.(undefined)
    if (selectedList && dailyList?.id === selectedList && dailyResult) {
      onDailyProgressChange?.(undefined)
    }
  }, [gameState, selectedList, activePollId])

  useEffect(() => {
    if (!dailyProgress?.startedAtMs) return
    setStartedAtMs(dailyProgress.startedAtMs)
  }, [dailyProgress?.startedAtMs])

  return gameState !== 'pre' && mode === 'single' ? (
    <>
      <EndGameView
        gameState={gameState}
        multiMode={multiMode}
        fullScreen={fullScreen}
        mode={mode}
        teamOneScore={0}
        teamTwoScore={0}
        teamWinner={0}
        selectedList={selectedList}
        activePollId={activePollId}
        hostPlayMode=""
        listItems={listItems}
        handleMiss={() => {}}
        handleNewGame={handleNewGame}
        handleHostPlayMode={() => {}}
        soloResult={soloResult}
        soloGuessCount={soloGuessCount}
        dailyList={dailyList}
        dailyResult={dailyResult as DailyListResult | undefined}
        soloStats={soloStats as UserSoloStats | undefined}
        soloXpAwarded={soloXpAwarded as number | undefined}
        loading={loading}
        listRated={listRated}
        appUserId={appUserId}
        user={user as UserState | undefined}
        doCreate={doCreate}
        topic={topic}
        sounds={sounds}
        handleEndGame={handleEndGame}
        handleRestartGame={handleRestartGame}
        handleCreateList={handleCreateList}
        handleRateList={handleRateList}
        setRateAlert={setRateAlert as (value: AlertInput) => void}
        historyPush={historyPush}
      />

      <SoloRankedGame
        items={listItems}
        maxGuesses={5}
        fullScreen={fullScreen}
        gameKey={soloGameKey}
        title={topic}
        contentType={activePollId ? 'poll' : 'trivia'}
        loading={loading || !listItems.length}
        refreshLabel={activePollId ? 'New Poll' : 'New List'}
        sounds={{
          click: sounds.click,
          drag: sounds.swipe,
          correct: sounds.celebrate,
          miss: sounds.select,
          submit: sounds.clickAlt,
          win: sounds.win,
          loss: sounds.loss,
        }}
        onRefresh={() => {
          handleEndGame()
          handleNewGame({ silentPollStart: true })
        }}
        onExit={() => {
          handlePreScreen()
        }}
        onGuessCountChange={(count) => {
          setSoloGuessCount(count)
          onHasGuessChange?.(count > 0)
        }}
        resumeState={dailyProgress?.resumeState}
        onStateChange={(state) => {
          if (selectedList && dailyList?.id === selectedList) {
            if (!state) {
              onDailyProgressChange?.(undefined)
              return
            }
            onDailyProgressChange?.({
              listId: selectedList,
              startedAtMs,
              resumeState: state,
              updatedAt: Date.now(),
            })
          }
        }}
        evaluateGuess={
          listItems[0]?.question
            ? async (order, requestId) => {
                const question = listItems[0].question
                const groupIds = order.map(
                  (id) =>
                    listItems.find(
                      (item: Item) => id === item.index + '-' + item.title,
                    )?.question?.groupId,
                )
                const response = await submitQuestionGuess(
                  question.pollId,
                  question.sessionId,
                  groupIds,
                  requestId,
                )
                if (activeQuestionSession.current !== question.sessionId)
                  throw new Error('This game has changed.')
                if (!response.success || !response.data)
                  throw new Error(
                    response.message || 'Could not submit this guess.',
                  )
                if (response.data.stats)
                  onSoloStatsUpdate?.(response.data.stats)
                if (response.data.result)
                  onSoloXpAwarded?.(response.data.result.xpDelta)
                return {
                  guessCount: response.data.guessCount,
                  lockedIds: listItems
                    .filter((item: Item) =>
                      response.data!.lockedIds.includes(item.question!.groupId),
                    )
                    .map((item: Item) => item.index + '-' + item.title),
                }
              }
            : undefined
        }
        onGameOver={(result, count, _lastOrder) => {
          const correctOrder = listItems
            .slice()
            .sort((a: Item, b: Item) => a.index - b.index)
            .map((item: Item) => `${item.index}-${item.title}`)
          const guessedOrder = _lastOrder || []
          const correct = guessedOrder.reduce((acc, id, idx) => {
            if (correctOrder[idx] === id) return acc + 1
            return acc
          }, 0)
          const total = correctOrder.length || listItems.length
          if (selectedList && dailyList?.id === selectedList) {
            const completionMs = Math.max(0, Date.now() - startedAtMs)
            if (appUserId) {
              setDailyResult({
                correct,
                total,
                guesses: count,
                completionMs,
                result,
              })
              submitDailyCompletion({
                listId: selectedList,
                correct,
                total,
                guesses: count,
                completionMs,
              })
                .then((res) => {
                  if (res.success && res.data) {
                    setDailyResult({
                      correct,
                      total,
                      guesses: count,
                      completionMs,
                      result,
                      rank:
                        res.data.accepted === false ? undefined : res.data.rank,
                      totalParticipants:
                        res.data.accepted === false
                          ? undefined
                          : res.data.totalParticipants,
                      percentile:
                        res.data.accepted === false
                          ? undefined
                          : res.data.percentile,
                      topPercentLabel:
                        res.data.accepted === false
                          ? undefined
                          : res.data.topPercentLabel,
                    })
                    if (res.data.soloStats) {
                      onSoloStatsUpdate?.(res.data.soloStats)
                    }
                    if (typeof res.data.soloXpAwarded === 'number') {
                      onSoloXpAwarded?.(res.data.soloXpAwarded)
                    }
                  }
                })
                .catch((e) => err(e))
            } else {
              const key = `daily_result_${selectedList}`
              const stored = localStorage.getItem(key)
              let canWrite = true
              if (stored) {
                try {
                  const parsed = JSON.parse(stored)
                  const ts = Number(parsed?.ts || 0)
                  if (ts && Date.now() - ts <= 24 * 60 * 60 * 1000) {
                    canWrite = false
                  }
                } catch {
                  // ignore
                }
              }
              if (canWrite) {
                localStorage.setItem(
                  key,
                  JSON.stringify({
                    correct,
                    total,
                    guesses: count,
                    completionMs,
                    ts: Date.now(),
                    result,
                    submitted: false,
                  }),
                )
                setDailyResult({
                  correct,
                  total,
                  guesses: count,
                  completionMs,
                  result,
                })
              }
            }
          } else if (appUserId && !listItems[0]?.question) {
            awardSoloPlay(correct, total)
              .then((res) => {
                if (res.success && res.data) {
                  onSoloStatsUpdate?.(
                    (res.data as { stats?: UserSoloStats }).stats,
                  )
                  onSoloXpAwarded?.(res.data.xpAwarded)
                }
              })
              .catch((e) => err(e))
          }
          setSoloResult(result)
          setSoloGuessCount(count)
          setGameState('over')
          if (selectedList) {
            handlePlayedList(selectedList)
          }
        }}
      />
    </>
  ) : null
}

export default SoloPlayView
